// What earlier candidates said about an OA, shared from their debriefs when they
// tick "Share with others interviewing here": companyDebriefs/{uid}_{cardId}.
// When another user gets an OA at the same company (and role, when there are
// enough), their plan leans on the topics those candidates got stuck on, and
// the OA card shows what they said. Names aren't shown; the writer's id is on
// the doc only so the Firestore rules can let them edit or remove it.

import { deleteDoc, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import type { ApiItem } from "./applicationCards";
import type { CodingTopicId } from "../data/prep/topics";
import { normalizeCompany } from "./dreamCompanies";
import type { Debrief, OaWent } from "./prepEvents";
import type { OaKind } from "./prepPlanner";

export const COMPANY_DEBRIEFS = "companyDebriefs";

export type CompanyDebrief = {
  /** The writer: only for the rules (edit and delete your own) */
  by: string;
  company: string;
  /** normalizeCompany(company), what other users' cards are matched on */
  companyKey: string;
  role: string;
  kind?: OaKind;
  went: OaWent;
  topics: CodingTopicId[];
  levels?: number;
  questions?: string[];
  note?: string;
  /** yyyy-mm-dd */
  day: string;
};

/** What other candidates said, put together for one OA */
export type Crowd = {
  /** How many debriefs this is from */
  people: number;
  /** All from the same role (otherwise the whole company) */
  sameRole: boolean;
  /** Where they got stuck, most often first */
  topics: CodingTopicId[];
  /** Their questions and notes, newest first */
  said: string[];
};

/** "software engineer intern" from "Software Engineer, Intern (2027)" */
const roleWords = (role?: string) =>
  new Set(
    (role ?? "")
      .toLowerCase()
      .replace(/\(.*?\)|\d{4}/g, " ")
      .split(/[^a-z]+/)
      .filter((w) => w.length > 1 && !["and", "the", "of", "for"].includes(w))
  );

/** Roughly the same role: most of the shorter title's words are in the other */
export function sameRole(a?: string, b?: string): boolean {
  const x = roleWords(a);
  const y = roleWords(b);
  if (!x.size || !y.size) return false;
  const [small, big] = x.size <= y.size ? [x, y] : [y, x];
  const shared = Array.from(small).filter((w) => big.has(w)).length;
  return shared / small.size >= 0.6;
}

/**
 * What others said about an OA at this card's company: the same role's
 * debriefs when there are any, else the whole company's. Null with none.
 */
export function crowdFor(card: ApiItem, debriefs: CompanyDebrief[]): Crowd | null {
  const key = normalizeCompany(card.company);
  const company = debriefs.filter((d) => d.companyKey === key);
  if (!company.length) return null;
  const role = company.filter((d) => sameRole(d.role, card.role));
  const pool = (role.length ? role : company).slice().sort((a, b) => b.day.localeCompare(a.day));
  const counts = new Map<CodingTopicId, number>();
  for (const d of pool) for (const t of d.topics) counts.set(t, (counts.get(t) ?? 0) + 1);
  return {
    people: pool.length,
    sameRole: role.length > 0,
    topics: Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([t]) => t),
    said: pool.flatMap((d) => [...(d.questions ?? []), ...(d.note ? [d.note] : [])]),
  };
}

/** One user's shared debrief for one card */
export const companyDebriefId = (uid: string, cardId: string) => `${uid}_${cardId}`;

/** Share a debrief (or stop sharing it): written when the debrief is saved */
export function shareDebrief(uid: string, card: ApiItem | undefined, debrief: Debrief, kind?: OaKind): Promise<void> {
  if (!card) return Promise.resolve();
  const ref = doc(db, COMPANY_DEBRIEFS, companyDebriefId(uid, card.id));
  if (!debrief.shared) return deleteDoc(ref).catch(() => undefined);
  const entry: CompanyDebrief = {
    by: uid,
    company: card.company,
    companyKey: normalizeCompany(card.company),
    role: card.role ?? "",
    ...(kind ? { kind } : {}),
    went: debrief.went,
    topics: debrief.topics,
    ...(debrief.levels !== undefined ? { levels: debrief.levels } : {}),
    ...(debrief.questions?.length ? { questions: debrief.questions } : {}),
    ...(debrief.note ? { note: debrief.note } : {}),
    day: debrief.day,
  };
  return setDoc(ref, { ...entry, at: serverTimestamp() });
}
